import { Box, Skeleton, Typography, Chip } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from 'recharts';
import { useDashboard } from '../DashboardContext';
import { useTranslation } from 'react-i18next';
import { DashboardInfoTip } from './DashboardInfoTip';
import DashboardWidget, { DashboardSizedChart } from './DashboardWidget';
import { useChartSurface } from '../dashboardChrome';

const DebtChart = ({
  data,
  defaultFill,
}: {
  data: { name: string; balance: number; credit_limit: number }[];
  defaultFill?: string;
}) => {
  const { currency } = useDashboard();
  const { t } = useTranslation();
  const chrome = useChartSurface();

  const formatCurrency = (value: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value.toFixed(0);
  };

  const formatTooltipValue = (value: number) => {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value) + ` ${currency}`;
  };

  const truncateName = (name: string, maxLen: number = 12) => {
    return name.length > maxLen ? name.substring(0, maxLen) + '...' : name;
  };

  const getBarColor = (balance: number, creditLimit: number) => {
    if (creditLimit <= 0) return '#ed6c02';
    const ratio = balance / creditLimit;
    if (ratio >= 0.9) return '#d32f2f';
    if (ratio >= 0.7) return '#ed6c02';
    return '#2e7d32';
  };

  if (data.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 200, color: 'text.secondary' }}>
        <Typography>{t('common:dashboard.noData')}</Typography>
      </Box>
    );
  }

  return (
    <DashboardSizedChart height={300}>
      <BarChart data={data} layout="vertical" margin={{ top: 5, right: 30, left: 5, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={chrome.grid} horizontal={false} />
        <XAxis type="number" tickFormatter={formatCurrency} fontSize={11} stroke={chrome.axis} />
        <YAxis
          type="category"
          dataKey="name"
          width={90}
          fontSize={11}
          stroke={chrome.axis}
          tickFormatter={(v) => truncateName(v)}
        />
        <Tooltip formatter={(value: number) => [formatTooltipValue(value), t('common:dashboard.debt.balance')]} />
        <Bar dataKey="balance" radius={[0, 4, 4, 0]} barSize={20} fill={defaultFill}>
          {!defaultFill && data.map((entry, idx) => (
            <Cell key={idx} fill={getBarColor(entry.balance, entry.credit_limit)} />
          ))}
        </Bar>
      </BarChart>
    </DashboardSizedChart>
  );
};

const DebtOverview = () => {
  const { topDebtors, topAgents, sectionLoading } = useDashboard();
  const { t } = useTranslation();

  if (sectionLoading.debt) {
    return (
      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
        {[1, 2].map((i) => (
          <DashboardWidget key={i} chartHeight={300} sx={{ flex: 1 }}>
            <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 2 }} />
          </DashboardWidget>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
      <DashboardWidget
        sx={{ flex: 1 }}
        chartHeight={300}
        header={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('common:dashboard.debt.topDebtors')}
            </Typography>
            <DashboardInfoTip helpKey="debt.topDebtors" />
            <Chip label={topDebtors.length} size="small" color="error" variant="outlined" />
          </Box>
        }
      >
        <DebtChart data={topDebtors} />
      </DashboardWidget>

      <DashboardWidget
        sx={{ flex: 1 }}
        chartHeight={300}
        header={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {t('common:dashboard.debt.agentBalances')}
            </Typography>
            <DashboardInfoTip helpKey="debt.agentBalances" />
            <Chip label={topAgents.length} size="small" color="info" variant="outlined" />
          </Box>
        }
      >
        <DebtChart data={topAgents} defaultFill="#0288d1" />
      </DashboardWidget>
    </Box>
  );
};

export default DebtOverview;